// Angular
import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
// Service
import {BroadcastEventModel, BroadcastService, LeftNavEventType} from '@cv-invision/core';
import {SchemasService} from '../services/schemas.service';
import {ViewsService} from '../services/views.service';
import {ROOM_POSITION} from '../sections-review/models/cvds/v3.3';
import {EVENT_MENU_SELECTED_LEVEL} from '../floating-sketch/floating-sketch.component';
import {SCHEMA_TYPE} from '../models/enums.model';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

export const EVENT_MENU_SELECTED = 'eventMenuSelected';
export const EVENT_MENU_RELOAD_TOP_MENU = 'eventMenuReloadTopMenu';

@Component({
	selector: 'cv-top-navigation-menu',
	templateUrl: './top-navigation-menu.component.html',
	styleUrls: ['./top-navigation-menu.component.scss']
})
export class TopNavigationMenuComponent implements OnInit, OnDestroy {

	// Contains all structures of the current Order
	private allStructures = [];
	// Select always first level and room
	public parentId = 'levels-0-0-0';
	public childId = 'rooms-0-0-0-0';
	public sectionSelected = '';
	// All Levels to Render
	public allLevels: any = [];
	// Information from the Order and Document
	get order(): any {
		return this.viewsServices.order;
	}
	get document(): any {
		return this.viewsServices.document;
	}
	// keep events clean
	private atComponentDestroy = new Subject();

	constructor(
		private broadcastService: BroadcastService,
		private schemasS: SchemasService,
		private route: ActivatedRoute,
		private viewsServices: ViewsService) {
		//
	}

	/**
	 * Initialize the Menu, keep the structure and listen to set Selected Item
	 */
	async ngOnInit(): Promise<void> {
		// Listen to select an Item
		this.broadcastService.onEvent(EVENT_MENU_SELECTED)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (broadcastEvent: BroadcastEventModel) => {
				const {event: {data}} = broadcastEvent;
				if (data) {
					this.setSelectedItems(data);
				}
			});

		this.broadcastService.onEvent(EVENT_MENU_RELOAD_TOP_MENU)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (broadcastEvent: BroadcastEventModel) => {
				this.loadTopMenu();
			});
	}

	/**
	 * Reload the Top Menu from Outside
	 */
	public reloadTopMenu(): void {
		let section = ROOM_POSITION.INTERIOR.toLowerCase();
		if (window.location.href.indexOf(section) === -1) {
			section = ROOM_POSITION.EXTERIOR.toLowerCase();
		}
		this.sectionSelected = section;

		this.loadTopMenu();
	}

	/**
	 * Load the Menu and listen for changes if necessary
	 * @private
	 */
	private loadTopMenu(): void {
		this.allStructures = this.schemasS.query(this.document, `$..structures[*]`) || [];
		// Get All Items to Render
		this.allLevels = this.getMenuItems();
	}

	/**
	 * Get leftNav menu items
	 */
	private getMenuItems(): any {
		let menuItems = [];

		switch (this.order.schema) {
			case SCHEMA_TYPE.CVDS_3:
				menuItems = this.getMenuItemsCVDS30();
				break;
			case SCHEMA_TYPE.CVDS_33:
				menuItems = this.getMenuItemsCVDS33();
				break;
		}

		return menuItems;
	}

	/**
	 * Get always the Level / Rooms Items for 3.3
	 * @private
	 */
	private getMenuItemsCVDS30(): any[] {
		let initLevelRoom = true;
		const baseUrl = `/desktop/order/dynamicform/${this.order.id}`;
		const menuItems = [];
		for (let structureI = 0; structureI < this.allStructures.length; structureI++) {
			for (let unitI = 0; unitI < this.allStructures[structureI].units.length; unitI++) {
				for (let levelI = 0; levelI < this.allStructures[structureI].units[unitI].levels.length; levelI++) {

					const rooms = [];
					for (let roomI = 0; roomI < this.allStructures[structureI].units[unitI].levels[levelI].rooms.length; roomI++) {
						const room: any = this.allStructures[structureI].units[unitI].levels[levelI].rooms[roomI];

						if (room.roomType
							&& room.roomType !== null
							&& room.roomType !== ''
						) {
							if (initLevelRoom) {
								this.parentId = `levels-${structureI}-${unitI}-${levelI}`;
								this.childId = `rooms-${structureI}-${unitI}-${levelI}-${roomI}`;
								initLevelRoom = false;
							}
							rooms.push({
								id: `rooms-${structureI}-${unitI}-${levelI}-${roomI}`,
								route: `${baseUrl}/room/${structureI}-${unitI}-${levelI}-${roomI}`,
								label: room.roomType,
								scrollable: true
							});
						}
					}
					if (rooms.length > 0) {
						menuItems.push({
							id: `levels-${structureI}-${unitI}-${levelI}`,
							route: `${baseUrl}/room/${structureI}-${unitI}-${levelI}`,
							label: this.allStructures[structureI].units[unitI].levels[levelI].levelNumber,
							icon: 'fa-door-open',
							scrollable: true,
							items: rooms
						});
					}
				}
			}
		}
		return menuItems;
	}

	/**
	 * Get always the Level / Rooms Items for 3.3
	 * @private
	 */
	private getMenuItemsCVDS33(): any[] {
		let initLevelRoom = true;
		const baseUrl = `/desktop/order/dynamicform/${this.order.id}`;
		const menuItems = [];
		for (let structureI = 0; structureI < this.allStructures.length; structureI++) {
			for (let unitI = 0; unitI < this.allStructures[structureI].units.length; unitI++) {
				for (let levelI = 0; levelI < this.allStructures[structureI].units[unitI].levels.length; levelI++) {

					const rooms = [];
					for (let roomI = 0; roomI < this.allStructures[structureI].units[unitI].levels[levelI].rooms.length; roomI++) {
						const room: any = this.allStructures[structureI].units[unitI].levels[levelI].rooms[roomI];
						const outSideArea = this.sectionSelected === 'exterior';

						if (room.position
							&& room.position.toLowerCase() === this.sectionSelected
							&& room.isOutsideArea === outSideArea
							&& room.roomName
							&& room.roomName !== null
							&& room.roomName !== ''
						) {
							if (initLevelRoom) {
								this.parentId = `levels-${structureI}-${unitI}-${levelI}`;
								this.childId = `rooms-${structureI}-${unitI}-${levelI}-${roomI}`;
								initLevelRoom = false;
							}
							rooms.push({
								id: `rooms-${structureI}-${unitI}-${levelI}-${roomI}`,
								route: `${baseUrl}${this.sectionSelected}/room/${structureI}-${unitI}-${levelI}-${roomI}`,
								label: room.roomName,
								scrollable: true
							});
						}
					}
					if (rooms.length > 0) {
						menuItems.push({
							id: `levels-${structureI}-${unitI}-${levelI}`,
							route: `${baseUrl}${this.sectionSelected}/room/${structureI}-${unitI}-${levelI}`,
							label: this.allStructures[structureI].units[unitI].levels[levelI].levelName,
							icon: 'fa-door-open',
							scrollable: true,
							items: rooms
						});
					}
				}
			}
		}
		return menuItems;
	}

	/**
	 * We iterate the entire structure to search the Unit and the Room that match the rooms id
	 * Rooms ID is the only Event registered to avoid multiple calls on the Scroll
	 * @private
	 */
	private setSelectedItems(focusedIndex: any): void {
		let selectedUnit = '';
		const currentLevel = {
			name: '',
			path: ''
		};
		const currentRoom = {
			path: ''
		};

		for (let sI = 0; sI < this.allStructures.length; sI++) {
			for (let uI = 0; uI < this.allStructures[sI].units.length; uI++) {
				for (let lI = 0; lI < this.allStructures[sI].units[uI].levels.length; lI++) {
					const selectedLevel = `levels-${sI}-${uI}-${lI}`;
					for (let roomI = 0; roomI < this.allStructures[sI].units[uI].levels[lI].rooms.length; roomI++) {
						if (focusedIndex === `rooms-${sI}-${uI}-${lI}-${roomI}`) {
							selectedUnit = `units-${sI}-${uI}`;
							this.parentId = selectedLevel;
							this.childId = focusedIndex;
							currentLevel.path = `InspectionReport.property.structures[${sI}].units[${uI}].levels[${lI}]`;
							currentLevel.name = this.allStructures[sI].units[uI].levels[lI].cv_Label || this.allStructures[sI].units[uI].levels[lI].levelName;

							currentRoom.path = `InspectionReport.property.structures[${sI}].units[${uI}].levels[${lI}].rooms[${roomI}]`;
							break;
						}
					}
				}
			}
		}
		if (currentRoom.path !== '') {
			const roomSelected = (this.schemasS.query(this.document, currentRoom.path) || [])[0];
			if (roomSelected && roomSelected.position) {
				let sectionSelected = this.sectionSelected;
				if (roomSelected.position === ROOM_POSITION.EXTERIOR && roomSelected.isOutsideArea === true) {
					sectionSelected = ROOM_POSITION.EXTERIOR.toLowerCase();
				} else if (roomSelected.position !== ROOM_POSITION.EXTERIOR && roomSelected.isOutsideArea === false) {
					sectionSelected = ROOM_POSITION.INTERIOR.toLowerCase();
				}

				if (sectionSelected !== this.sectionSelected) {
					this.sectionSelected = sectionSelected;
					this.loadTopMenu();
				}
			}
		}

		// Send the Request of all elements selected
		this.broadcastService.broadcast({
			name: LeftNavEventType.NAV_ITEM_SCROLLED,
			event: { data: focusedIndex }
		});
		this.broadcastService.broadcast({ name: EVENT_MENU_SELECTED_LEVEL,
			event: {
				data: {
					scrollableIndex: this.parentId,
					currentLevel: currentLevel,
					sectionSelected: this.sectionSelected
				}
			}
		});
	}

	/**
	 * Open a parent item and select first children
	 * @param item
	 */
	public openParentItem(item: any): void {
		if (item.items && item.items.length > 0) {
			setTimeout(() => {
				this.openChildItem(item.items[0]);
			});
		}
	}

	/**
	 * Open Item
	 * @param item
	 */
	public openChildItem(item: any): void {
		this.broadcastService.broadcast({name: LeftNavEventType.NAV_ITEM_SCROLLED, event: {data: item.id}});
		this.broadcastService.broadcast({name: LeftNavEventType.NAV_ITEM_CLICKED, event: {data: item}});
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
